import { Store } from 'typesafe-actions';

import env from '@travel/env';
import { DEVICE_ID } from '@travel/traveler-core/constants';

import { EMPTY_OBJECT } from 'constants/defaultValue';

import { PATH_TO_RAT } from '@travel/traveler-core/constants/ratAnalytics';

type RequestInterceptorOptions = {
  options: any;
  path: string;
  url: string;
  isThirdPartyApi: boolean;
};

/**
 * Returns a rat key for a page, based on the current path and search params
 * @param path - Current path where the request is made
 * @param searchParams - Search params of the current path, used for dated/undated distinction
 * */
export function getRatKeyFromPath(path: string, searchParams?: string): string | undefined {
  const isDated =
    searchParams?.includes('endServiceDate') || searchParams?.includes('startServiceDate');
  for (const [key, value] of Object.entries(PATH_TO_RAT)) {
    const regex = new RegExp(key);

    if (regex.test(path)) {
      if (typeof value === 'object') {
        return isDated ? value.dated : value.undated;
      }

      return value as string;
    }
  }
  return undefined;
}

/**
 * Intercept requests and enrich them with additional headers
 * @param store - Redux store
 * @param originalUrl - Original url of the request on SSR. Used to get the page name for the headers
 * */
const requestInterceptor = (store: Store, originalUrl?: string) => ({
  options,
  path,
  url,
  isThirdPartyApi,
}: RequestInterceptorOptions) => {
  const state = store.getState();
  const { normalizedCookies } = state._httpRequest;
  const { currency, language, market } = state._i18n;

  const { isMobile, isTablet, os } = state.userAgent.userAgent;
  const platform = isMobile ? 'SP Web' : isTablet ? 'Tablet Web' : 'PC Web';

  // Gets the page name from redux router state if present or creates it from a mapping,
  // based on the current path and search params. If no mapping is found, it uses a default value.
  const currentPath = originalUrl || state.router.location.location.pathname;
  const keyFromPath = getRatKeyFromPath(currentPath, state.router.location.location.search);
  const pageName =
    keyFromPath || state.router.previousPageName || `no_defined_rat_key_${currentPath}`;

  let verbose = false;
  if (normalizedCookies) {
    verbose = 'trv-very-verbose' in normalizedCookies;
  }

  // To make sure that we will send user credential only if url is under the same origin for 3rd party
  const credentials = isThirdPartyApi ? 'same-origin' : options.credentials;

  const {
    item: { token },
  } = store.getState().authCode;
  if (env('UNIVERSAL_ISGATED')) {
    return {
      url: 'http://localhost:5555/' + path,
      options: {
        ...options,
        credentials,
        headers: isThirdPartyApi
          ? EMPTY_OBJECT
          : {
              ...options.headers,
              'trv-device-id': normalizedCookies[DEVICE_ID],
              'trv-currency': currency,
            },
      },
    };
  }

  const result = {
    url,
    options: {
      ...options,
      credentials,
      headers: isThirdPartyApi
        ? EMPTY_OBJECT
        : {
            'trv-device-id': normalizedCookies[DEVICE_ID],
            'trv-currency': currency,
            'trv-language': language,
            'trv-market': market?.marketCode,
            'trv-platform': platform,
            'trv-os': os,
            'trv-page': pageName,
            ...options.headers,
          },
    },
    verbose,
  };

  if (token && !isThirdPartyApi) {
    result.options.headers.Authorization = token;
  }

  return result;
};

export default requestInterceptor;
